import axios from 'axios';
import { callingService } from './callingService';
import { AUTH_URL } from '../config/apiUrls';
import { getCurrentUserIdFromToken } from '../utils/authUser';
import type {
  ChatErrorEvent,
  ChatIncomingMessageEvent,
  ChatMessage,
  ChatReadEvent,
  ChatSentEvent,
  ChatThreadSummary,
} from '../types/chat';

const chatClient = axios.create({
  baseURL: AUTH_URL,
  headers: { 'Content-Type': 'application/json' },
});

chatClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

chatClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  },
);

type ThreadMessagesState = {
  items: ChatMessage[];
  loading: boolean;
  loadingMore: boolean;
  hasLoaded: boolean;
  hasMore: boolean;
};

type ChatSnapshot = {
  threads: ChatThreadSummary[];
  threadsLoading: boolean;
  activeThreadId: number | null;
  currentRecipientUserId: number | null;
  currentRecipientEmail: string | null;
  messagesByThreadId: Record<number, ThreadMessagesState>;
  totalUnreadCount: number;
  error: string | null;
  socketConnected: boolean;
};

const defaultThreadMessagesState = (): ThreadMessagesState => ({
  items: [],
  loading: false,
  loadingMore: false,
  hasLoaded: false,
  hasMore: true,
});

let snapshot: ChatSnapshot = {
  threads: [],
  threadsLoading: false,
  activeThreadId: null,
  currentRecipientUserId: null,
  currentRecipientEmail: null,
  messagesByThreadId: {},
  totalUnreadCount: 0,
  error: null,
  socketConnected: false,
};

let boundSocket: {
  on: (event: string, handler: (...args: any[]) => void) => void;
  connected?: boolean;
} | null = null;

const subscribers = new Set<() => void>();

const emitChange = () => {
  subscribers.forEach((listener) => listener());
};

const sortThreads = (threads: ChatThreadSummary[]) =>
  [...threads].sort((a, b) => {
    const aDate = Date.parse(a.lastMessageAt ?? a.updatedAt ?? a.createdAt);
    const bDate = Date.parse(b.lastMessageAt ?? b.updatedAt ?? b.createdAt);
    return bDate - aDate;
  });

const recalcUnread = () => {
  snapshot.totalUnreadCount = snapshot.threads.reduce(
    (sum, thread) => sum + Math.max(0, thread.unreadCount || 0),
    0,
  );
};

const setSnapshot = (updater: (prev: ChatSnapshot) => ChatSnapshot) => {
  snapshot = updater(snapshot);
  recalcUnread();
  emitChange();
};

const ensureThreadState = (threadId: number): ThreadMessagesState =>
  snapshot.messagesByThreadId[threadId] ?? defaultThreadMessagesState();

const upsertThread = (thread: ChatThreadSummary) => {
  setSnapshot((prev) => {
    const existing = prev.threads.filter((item) => item.id !== thread.id);
    return {
      ...prev,
      threads: sortThreads([...existing, thread]),
    };
  });
};

const upsertMessage = (threadId: number, message: ChatMessage) => {
  setSnapshot((prev) => {
    const existing = ensureThreadState(threadId);
    const nextItems = existing.items.filter((item) => item.id !== message.id);
    nextItems.push(message);
    nextItems.sort((a, b) => a.id - b.id);
    return {
      ...prev,
      messagesByThreadId: {
        ...prev.messagesByThreadId,
        [threadId]: {
          ...existing,
          hasLoaded: true,
          items: nextItems,
        },
      },
    };
  });
};

const mergeMessages = (threadId: number, messages: ChatMessage[], prepend = false) => {
  setSnapshot((prev) => {
    const existing = ensureThreadState(threadId);
    const byId = new Map<number, ChatMessage>();
    if (prepend) {
      messages.forEach((message) => byId.set(message.id, message));
      existing.items.forEach((message) => byId.set(message.id, message));
    } else {
      existing.items.forEach((message) => byId.set(message.id, message));
      messages.forEach((message) => byId.set(message.id, message));
    }
    const nextItems = Array.from(byId.values()).sort((a, b) => a.id - b.id);
    return {
      ...prev,
      messagesByThreadId: {
        ...prev.messagesByThreadId,
        [threadId]: {
          ...existing,
          items: nextItems,
          hasLoaded: true,
          loading: false,
          loadingMore: false,
          hasMore: messages.length > 0,
        },
      },
    };
  });
};

const clearThreadUnread = (threadId: number) => {
  setSnapshot((prev) => ({
    ...prev,
    threads: prev.threads.map((thread) =>
      thread.id === threadId ? { ...thread, unreadCount: 0 } : thread,
    ),
  }));
};

const getSocket = () => {
  const token = localStorage.getItem('token');
  if (!token) return null;
  return callingService.connect(token) as unknown as {
    connected?: boolean;
    on: (event: string, handler: (...args: any[]) => void) => void;
    emit: (event: string, payload?: any) => void;
  };
};

const bindSocketHandlers = () => {
  const socket = getSocket();
  if (!socket || boundSocket === socket) return;
  boundSocket = socket;

  const onConnect = () => {
    setSnapshot((prev) => ({ ...prev, socketConnected: true }));
    void chatService.refreshThreads();
    if (snapshot.activeThreadId != null) {
      void chatService.loadThreadMessages(snapshot.activeThreadId, { refresh: true });
    }
  };

  const onDisconnect = () => {
    setSnapshot((prev) => ({ ...prev, socketConnected: false }));
  };

  const onSent = (event: ChatSentEvent) => {
    upsertThread(event.thread);
    upsertMessage(event.thread.id, event.message);
  };

  const onMessage = (event: ChatIncomingMessageEvent) => {
    upsertThread(event.thread);
    upsertMessage(event.thread.id, event.message);
    if (snapshot.activeThreadId === event.thread.id) {
      void chatService.markThreadRead(event.thread.id);
    }
  };

  const onRead = (event: ChatReadEvent) => {
    if (event.thread) {
      upsertThread(event.thread);
    } else {
      clearThreadUnread(event.threadId);
    }

    const currentUserId = getCurrentUserIdFromToken();
    if (currentUserId == null) return;

    setSnapshot((prev) => {
      const existing = ensureThreadState(event.threadId);
      return {
        ...prev,
        messagesByThreadId: {
          ...prev.messagesByThreadId,
          [event.threadId]: {
            ...existing,
            items: existing.items.map((message) =>
              message.senderUserId === currentUserId &&
              (event.readByUserId == null || message.recipientUserId === event.readByUserId)
                ? {
                    ...message,
                    status: 'read',
                    readAt: message.readAt ?? new Date().toISOString(),
                  }
                : message,
            ),
          },
        },
      };
    });
  };

  const onError = (event: ChatErrorEvent) => {
    setSnapshot((prev) => ({ ...prev, error: event.message || 'Chat failed' }));
  };

  socket.on('connect', onConnect);
  socket.on('disconnect', onDisconnect);
  socket.on('chat:sent', onSent);
  socket.on('chat:message', onMessage);
  socket.on('chat:read', onRead);
  socket.on('chat:error', onError);

  if (socket.connected) onConnect();
};

const getApiErrorMessage = (error: unknown, fallback: string) => {
  const data = (error as { response?: { data?: { message?: string | string[] } } })?.response?.data;
  const messageValue = data?.message;
  if (Array.isArray(messageValue)) return messageValue.join(', ');
  if (typeof messageValue === 'string') return messageValue;
  return fallback;
};

class ChatService {
  subscribe(listener: () => void) {
    subscribers.add(listener);
    return () => subscribers.delete(listener);
  }

  getSnapshot() {
    return snapshot;
  }

  ensureConnected() {
    bindSocketHandlers();
    return getSocket();
  }

  setActiveThread(threadId: number | null, recipient?: { userId?: number | null; email?: string | null }) {
    setSnapshot((prev) => ({
      ...prev,
      activeThreadId: threadId,
      currentRecipientUserId: recipient?.userId ?? null,
      currentRecipientEmail: recipient?.email ?? null,
      error: null,
    }));
  }

  async refreshThreads() {
    setSnapshot((prev) => ({ ...prev, threadsLoading: true, error: null }));
    try {
      const response = await chatClient.get<ChatThreadSummary[]>('/chats');
      setSnapshot((prev) => ({
        ...prev,
        threads: sortThreads(response.data),
        threadsLoading: false,
      }));
    } catch (error) {
      setSnapshot((prev) => ({
        ...prev,
        threadsLoading: false,
        error: getApiErrorMessage(error, 'Failed to load chats'),
      }));
      throw error;
    }
  }

  async loadThreadMessages(
    threadId: number,
    options?: { beforeId?: number; limit?: number; refresh?: boolean },
  ) {
    const beforeId = options?.beforeId;
    const limit = Math.min(options?.limit ?? 50, 100);
    const existing = ensureThreadState(threadId);

    setSnapshot((prev) => ({
      ...prev,
      messagesByThreadId: {
        ...prev.messagesByThreadId,
        [threadId]: {
          ...existing,
          loading: !beforeId,
          loadingMore: Boolean(beforeId),
        },
      },
      error: null,
    }));

    try {
      const response = await chatClient.get<ChatMessage[]>(`/chats/${threadId}/messages`, {
        params: { beforeId, limit },
      });
      if (options?.refresh && !beforeId) {
        setSnapshot((prev) => ({
          ...prev,
          messagesByThreadId: {
            ...prev.messagesByThreadId,
            [threadId]: {
              ...existing,
              items: [],
              loading: false,
              loadingMore: false,
              hasLoaded: false,
              hasMore: true,
            },
          },
        }));
      }
      mergeMessages(threadId, response.data, Boolean(beforeId));
      return response.data;
    } catch (error) {
      setSnapshot((prev) => ({
        ...prev,
        error: getApiErrorMessage(error, 'Failed to load chat messages'),
        messagesByThreadId: {
          ...prev.messagesByThreadId,
          [threadId]: {
            ...existing,
            loading: false,
            loadingMore: false,
          },
        },
      }));
      throw error;
    }
  }

  async markThreadRead(threadId: number) {
    try {
      await chatClient.post(`/chats/${threadId}/read`);
      this.ensureConnected()?.emit('chat:mark-read', { threadId });
      clearThreadUnread(threadId);
    } catch (error) {
      setSnapshot((prev) => ({
        ...prev,
        error: getApiErrorMessage(error, 'Failed to mark chat as read'),
      }));
      throw error;
    }
  }

  async sendMessage(body: string, recipientUserId?: number | null) {
    const normalized = body.trim();
    if (!normalized) throw new Error('Message cannot be empty');

    const socket = this.ensureConnected();
    if (!socket) throw new Error('Missing auth token');

    const activeThread = snapshot.threads.find((thread) => thread.id === snapshot.activeThreadId) ?? null;
    const resolvedRecipient =
      recipientUserId ??
      activeThread?.otherUserId ??
      snapshot.currentRecipientUserId;

    if (resolvedRecipient == null) {
      throw new Error('Missing recipient for chat message');
    }

    setSnapshot((prev) => ({ ...prev, error: null }));
    socket.emit('chat:send', {
      recipientUserId: resolvedRecipient,
      body: normalized,
    });
  }

  clearError() {
    setSnapshot((prev) => ({ ...prev, error: null }));
  }
}

export const chatService = new ChatService();
